import { Inject } from '@wendellhu/redi';
import { IBackendLogService, type BackendLogService } from '@/backend/core';
import dayjs from 'dayjs';
import { ISolutionRepository } from './interfaces/solution.repository.interface';
import { CaseService } from '../case/case.service';
import type {
  ActiveSolutionCardsQueryParams,
  CaseMetaDto,
  HomeCompletionStatsDto,
  HomeCompletionStatsQueryParams,
  SolutionAppendsQueryParams,
  SolutionHomeCardDto,
  SolutionImagingParseStateDto,
  SolutionListItemDto,
  SolutionPlanSetupDto,
} from './dto/solution.dto';
import { SolutionHomeCardMapper } from './mappers/solution-home.mapper';
import { SolutionListMapper } from './mappers/solution-list.mapper';
import { SolutionCreateMapper } from './mappers/solution-create.mapper';
import type { SolutionCreateParams } from './dto/solution-create.dto';
import type { EngrEngineeringDTO } from '@holoview/dao-gen/generated';
import { uuidv7 } from '@/shared/uuid';
import { normalizeSolutionType } from '@/shared/enums/solution-type';

type PlanSetupInput = Pick<SolutionPlanSetupDto, 'planSetupAi' | 'planSetupHolo' | 'planSetupMdps'>;

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(item => String(item ?? '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      return toStringArray(JSON.parse(trimmed));
    } catch {
      return [trimmed];
    }
  }
  return [];
}

function toPlanSetupDto(row: EngrEngineeringDTO): SolutionPlanSetupDto {
  return {
    solutionId: Number(row.id ?? 0) || 0,
    solutionType: normalizeSolutionType(row.type),
    planSetupAi: toStringArray(row.plan_setup_ai),
    planSetupHolo: toStringArray(row.plan_setup_holo),
    planSetupMdps: toStringArray(row.plan_setup_mdps),
  };
}

export class SolutionService {
  private readonly log: BackendLogService;

  constructor(
    @Inject(ISolutionRepository) private readonly repo: ISolutionRepository,
    @Inject(CaseService) private readonly caseService: CaseService,
    @IBackendLogService rootLogger: BackendLogService,
  ) {
    this.log = rootLogger.child('SolutionService');
  }

  async getCaseMeta(solutionId: number): Promise<CaseMetaDto> {
    const data = await this.repo.getCaseMeta(solutionId);
    if (!data) {
      this.log.warn('solution.case_meta.not_found', {
        context: { solutionId },
      });
      throw new Error(`Solution not found: ${solutionId}`);
    }

    const [versions, mdpsProductionOrderId] = await Promise.all([
      this.repo.getVersions(solutionId),
      this.repo.getMdpsProductionOrderId({
        solutionId,
        caseId: data.case_maindata_id,
      }),
    ]);

    const meta: CaseMetaDto = {
      id: data.id,
      name: data.name ?? '',
      solutionStatus: String(data.status ?? ''),
      caseStatus: String(data.case_maindata?.status ?? ''),
      caseMaindataId: data.case_maindata_id ?? 0,
      omsOrderId: data.oms_order_id ?? 0,
      mdpsProductionOrderId,
      gaSiteId: data.case_maindata?.ga_site_id ?? 0,
      uuid: data.uuid ?? '',
      imagingId: null,
      seriesId: data.series_id ?? null,
      patient: this._extractPatient(data),
      versions: (versions ?? []).map((v: { id: number; version?: number; status?: string; created_at?: string }) => ({
        id: v.id,
        version: Number(v.version ?? 1),
        status: v.status ?? '',
        createdAt: v.created_at ?? '',
      })).sort((a: { version: number }, b: { version: number }) => b.version - a.version),
      solutionType: normalizeSolutionType(data.type),
    };

    return meta;
  }

  async getVersions(solutionId: number) {
    return this.repo.getVersions(solutionId);
  }

  async findMdpsProductionOrderIds(solutionIds: number[]): Promise<Map<number, number>> {
    return this.repo.findMdpsProductionOrderIds(solutionIds);
  }

  async setImagingSelection(solutionId: number, imagingId: number | null, activeSeriesId: string): Promise<void> {
    await this.repo.setImagingSelection(solutionId, imagingId, activeSeriesId);
  }

  async updateStatus(params: {
    solutionId: number;
    caseId: number;
    versionId: number;
    status: string;
  }): Promise<void> {
    this.log.debug('solution.status.update.started', {
      context: {
        solutionId: params.solutionId,
        caseId: params.caseId,
        versionId: params.versionId,
        status: params.status,
      },
    });
    await this.repo.updateStatus(params.versionId, params.status);
    await this.repo.updateEngineeringStatus(params.solutionId, params.status);
    await this.caseService.updateStatus(params.caseId, params.status);
  }

  async createVersion(solutionId: number): Promise<{ id: number }> {
    const versions = await this.repo.getVersions(solutionId);
    const maxVersion = (versions ?? []).reduce(
      (max: number, v: { version?: number }) => Math.max(max, Number(v.version ?? 0)),
      0,
    );
    return this.repo.createVersion({
      engineering_id: solutionId,
      version: maxVersion + 1,
      status: 'design',
    });
  }

  async createSolution(params: SolutionCreateParams): Promise<EngrEngineeringDTO> {
    return this.repo.createSolution({
      ...SolutionCreateMapper.toCreateParams(params),
      status: 'design',
      is_publish: 0,
      uuid: uuidv7(),
    });
  }

  async findSolutionsByCase(caseId: number): Promise<SolutionListItemDto[]> {
    const rows = await this.repo.findSolutionsByCase(caseId);
    return SolutionListMapper.toList(rows);
  }

  async findLatestSolutionPlanSetupByCase(caseId: number): Promise<SolutionPlanSetupDto | null> {
    const rows = await this.repo.findSolutionsByCase(caseId);
    const latest = rows[0];
    return latest ? toPlanSetupDto(latest) : null;
  }

  async updateCasePlanSetup(caseId: number, planSetup: PlanSetupInput): Promise<void> {
    const rows = await this.repo.findSolutionsByCase(caseId);
    const latest = rows[0];
    if (!latest?.id) {
      this.log.warn('solution.plan_setup.update.skipped_no_solution', {
        context: { caseId },
      });
      return;
    }
    await this.repo.updatePlanSetup(latest.id, {
      plan_setup_ai: [...planSetup.planSetupAi],
      plan_setup_holo: [...planSetup.planSetupHolo],
      plan_setup_mdps: [...planSetup.planSetupMdps],
    });
  }

  async fetchSolutionsWithAppends(params: SolutionAppendsQueryParams): Promise<Record<string, unknown>[]> {
    return this.repo.fetchSolutionsWithAppends(params);
  }

  async getActiveSolutionCards(params: ActiveSolutionCardsQueryParams): Promise<SolutionHomeCardDto[]> {
    const rows = await this.repo.getActiveSolutionCards(params);
    return SolutionHomeCardMapper.toHomeCardList(rows, ids => this.caseService.findCasesWithArchiveByIds(ids));
  }

  async getHomeCompletionStats(params: HomeCompletionStatsQueryParams): Promise<HomeCompletionStatsDto> {
    const now = dayjs();
    const format = 'YYYY-MM-DD HH:mm:ss';
    const [todayCompleted, monthCompleted, careerCompleted] = await Promise.all([
      this.repo.countCompletedTaskDispatches({
        currentUserId: params.currentUserId,
        completedByUserIds: params.completedByUserIds,
        completedAtStart: now.startOf('day').format(format),
        completedAtEnd: now.endOf('day').format(format),
      }),
      this.repo.countCompletedTaskDispatches({
        currentUserId: params.currentUserId,
        completedByUserIds: params.completedByUserIds,
        completedAtStart: now.startOf('month').format(format),
        completedAtEnd: now.endOf('month').format(format),
      }),
      this.repo.countCompletedTaskDispatches({
        currentUserId: params.currentUserId,
        completedByUserIds: params.completedByUserIds,
      }),
    ]);
    return {
      todayCompleted,
      monthCompleted,
      careerCompleted,
      excellentReviews: 0,
    };
  }

  async getImagingParseState(solutionId: number): Promise<SolutionImagingParseStateDto | null> {
    return this.repo.getImagingParseState(solutionId);
  }

  private _extractPatient(data: {
    case_maindata?: {
      extend?: Record<string, unknown>;
      case_maindata_archive?: Record<string, unknown> | Record<string, unknown>[];
      caseMaindataArchive?: Record<string, unknown> | Record<string, unknown>[];
      archive?: Record<string, unknown> | Record<string, unknown>[];
      name?: unknown;
    };
  }): { name: string; gender: string; age: string } {
    const caseMain = data?.case_maindata;
    const archive = this._firstRecord(
      caseMain?.extend ??
        caseMain?.case_maindata_archive ??
        caseMain?.caseMaindataArchive ??
        caseMain?.archive,
    );
    return {
      name: String(archive?.name ?? caseMain?.name ?? ''),
      gender: String(archive?.gender ?? ''),
      age: String(archive?.age ?? ''),
    };
  }

  private _firstRecord(value: unknown): Record<string, unknown> | null {
    const item = Array.isArray(value) ? value[0] : value;
    return typeof item === 'object' && item !== null ? item as Record<string, unknown> : null;
  }
}
